import type { NavigationGuardNext, RouteLocationNormalized } from "vue-router";
import {
  clearSessionRejection,
  hasAuthSession,
  isReturningVisitor,
  markSessionSeen,
  purgeLegacySession,
} from "../common/helpers/auth/authBrowserState";
import { AUTO_ENTRY_QUERY } from "../common/composables/useAuthEntry";

function requiresAuth(to: RouteLocationNormalized): boolean {
  return to.matched.some((record) => record.meta.requiresAuth);
}

/**
 * Единая авторизация (CAB-638): признак сессии — кука {{eplex_token}}. Формы входа
 * у ЛК нет, её отдаёт REG на своём домене.
 *
 * Два исхода при отсутствии сессии — намеренно:
 *
 * # *человек уже входил в этом браузере* — уводим на вход без лишнего клика: ему
 *   нечего рассказывать про продукт. Сам уход делает страница входа, а не гард:
 *   адрес REG спрашивается у сервера (Ф1), то есть асинхронно, а гард обязан
 *   ответить синхронно;
 * # *пришёл впервые* — на страницу-приглашение. Он ещё не решил, нужен ли ему вход
 *   вообще, и именно здесь он узнаёт про регистрацию за минуту.
 *
 * Защиты от петли «нет куки → уход → возврат без куки → уход» здесь больше нет, и это
 * не упущение: её держит сервер (Ф2). Он единственный знает ПРИЧИНУ отказа, и второй
 * отказ подряд отдаёт уже без адреса входа — уходить становится некуда, и страница
 * показывает «войти не удалось» вместо мигания.
 */
export function resolveUnifiedAuth(to: RouteLocationNormalized, next: NavigationGuardNext): void {
  if (hasAuthSession()) {
    markSessionSeen();
    // Метка отклонённой сессии относится к прошлому токену: раз сессия жива, значит
    // кука уже другая и метка не совпадает. Снимаем её, чтобы во вкладке не копился
    // отпечаток, к которому больше нечему относиться (CAB-680)
    clearSessionRejection();
    // Следы прежней схемы стираем при первом же появлении куки: иначе старый токен
    // из localStorage уедет в заголовок и человек окажется вчерашним пользователем
    purgeLegacySession();

    if (to.name === "login") {
      next("/");
      return;
    }

    next();
    return;
  }

  if (to.name === "login" || !requiresAuth(to)) {
    next();
    return;
  }

  if (isReturningVisitor()) {
    next({ name: "login", query: { redirect: to.fullPath, [AUTO_ENTRY_QUERY]: "1" } });
    return;
  }

  next({ name: "login", query: { redirect: to.fullPath } });
}

export function resolveAuthNavigation(to: RouteLocationNormalized, next: NavigationGuardNext): void {
  resolveUnifiedAuth(to, next);
}
